import { Env,Host,Component,Element, State, h, Prop } from '@stencil/core';
import { isBrowser } from '../../constants/constants';
import { checkoutLink } from '../../constants/constants';
import { formatPrice } from '../../utils/price';

import { rollbar } from '../../utils/rollbar';

interface CartResponse {
  account: {
    email: string | null;
  },
  token: string,
  inventory_id: number | null,
  postal_code: string,
  total_items: number;
  items: Array<{
    color_id: number,
    color_name: string,
    design_composite_id: string,
    design_name: string,
    design_updated_at: number,
    quantity: number,
    quote_quantities: {
      YS: number,
      YM: number,
      YL: number,
      XS: number,
      S: number,
      M: number,
      L: number,
      XL: number,
      XXL: number,
      XXXL: number,
      XXXXL:number,
      XXXXXL: number,
    },
    style_name: string,
    sub_total: number,
    unit_price: number,
    created_at: number,
    updated_at: number,
    delivery_method_key: string,
    order_type: string | null,
  }>;
}
@Component({
  tag: 'ci-cart',
  styleUrl: 'ci-cart.scss',
  shadow: true,
})
export class CiCart {
  @Prop() detail: boolean;
  @Prop() pricing: boolean;
  @Prop() detailedQuantities: boolean;
  @State() cart?: CartResponse;
  @Element() $el: HTMLElement;

  // Static class variables shared across all instances
  private static isInitialized = false;
  private static sharedCart: CartResponse;
  private static listeners: Set<() => void> = new Set();
  private   dialog!: HTMLCiDialogElement


  private static async refreshCart(pricing: boolean) {
    CiCart.sharedCart = await CiCart.fetchCart(pricing);
    // Notify all instances of the update
    CiCart.listeners.forEach(listener => listener());
  }

  private async onUpdateCookie() {
    return CiCart.fetchCart(this.pricing);
  }

  private static async fetchCart(pricing: boolean) {
    try {
      const response = await window.fetch(`${Env.CART_URL}?include_pricing=${pricing}&src=ci-cart`);
      return await response.json() as CartResponse;
    } catch (error) {
      rollbar.error('Error during fetching cart checkout', error);
      return undefined;
    }
  }

  private updateLocalCount = () => {
    this.cart = CiCart.sharedCart;
  };

  async componentWillLoad() {
    // Only initialize once across all instances
    if (!CiCart.isInitialized) {
      CiCart.isInitialized = true;
      CiCart.refreshCart(this.pricing).catch(error => rollbar.error('Error refreshing cart cookie:', error));
      window.addEventListener('cartCookieUpdated', this.onUpdateCookie.bind(this));
      window.addEventListener('ciCartUpdated', this.onUpdateCookie.bind(this));
    }

    // Add this instance's listener
    CiCart.listeners.add(this.updateLocalCount);
    this.cart = CiCart.sharedCart;
  }

  disconnectedCallback() {
    CiCart.listeners.delete(this.updateLocalCount);

    // Only remove global listener if no instances remain
    if (CiCart.listeners.size === 0) {
      window.removeEventListener('cartCookieUpdated', this.onUpdateCookie.bind(this));
      window.removeEventListener('ciCartUpdated', this.onUpdateCookie.bind(this));
      CiCart.isInitialized = false;
    }
  }

  redirectToCart() {
    window.location.href = checkoutLink;
  }

  showCartLayout(event) {
    if(!this.detail) {
      return;
    }
    this.dialog.openModal(event);
  }
  hideCartLayout(event) {
    if(!this.detail) {
      return;
    }
    this.dialog.closeModal(event);
  }

  render() {
    const cartCount = isBrowser ? this.cart?.total_items : 0;
    const cartItemsGroupByDesignName = this.cart?.items.reduce((acc, item) => {
      const designCompositeId = item.design_composite_id;
      if (!acc[designCompositeId]) {
        acc[designCompositeId] = [];
      }
      acc[designCompositeId].push(item);
      return acc;
    }, {} as Record<string, Array<CartResponse['items'][0]>>);
    const isCartEmpty= this.cart?.items.length === 0 || !this.cart;
    return (
      <Host
        onMouseEnter={this.showCartLayout.bind(this)}
        onMouseLeave={this.hideCartLayout.bind(this)}
      >
        <a class="ghf-icon-button ciHeader-cart-icon-button"
          href={checkoutLink}
          data-testid="cart-global-header"
          onTouchStart={(event) => this.showCartLayout(event)}
          onTouchEnd={(event) => event.preventDefault()}
          aria-label="Cart">
          <div class="cart-wrapper">
            {cartCount > 0 && (
              <div class="ciHeader-cart-count">
                {cartCount}
              </div>
            )}
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M2 1C1.44772 1 1 1.44772 1 2C1 2.55228 1.44772 3 2 3H3.14551L5.9456 20.7339C6.06072 21.463 6.68914 22 7.42725 22H19C19.5523 22 20 21.5523 20 21C20 20.4477 19.5523 20 19 20H7.85449L7.5387 18H18.3308C19.7807 18 21.0231 16.9631 21.2825 15.5367L22.8767 6.76833C23.0441 5.84757 22.3368 5 21.4009 5H5.48607L5.0544 2.26606C4.93928 1.53698 4.31086 1 3.57275 1H2ZM5.80186 7L7.22292 16H18.3308C18.8141 16 19.2283 15.6544 19.3147 15.1789L20.8018 7H5.80186Z"
                fill="#4A4A4A"
              />
            </svg>
          </div>
          <span part='text'>Cart</span>
        </a>
        {this.detail && (
          <ci-dialog name="Cart Preview" ref={(el) => this.dialog = el}>
            <div slot="dialog-content" class="dialog-content">
              <div class="cart-preview-list">
                {isCartEmpty && (
                  <div class="cart-preview-empty">
                    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="37" viewBox="0 0 36 37" fill="none">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M3 2C2.17157 2 1.5 2.67157 1.5 3.5C1.5 4.32843 2.17157 5 3 5H4.71826L8.9184 31.6009C9.09108 32.6945 10.0337 33.5 11.1409 33.5H28.5C29.3284 33.5 30 32.8284 30 32C30 31.1716 29.3284 30.5 28.5 30.5H11.7817L11.3081 27.5H27.4963C29.6711 27.5 31.5346 25.9447 31.9237 23.805L34.315 10.6525C34.5662 9.27135 33.5051 8 32.1013 8H8.22911L7.5816 3.89908C7.40892 2.80547 6.4663 2 5.35913 2H3ZM8.70279 11L10.8344 24.5H27.4963C28.2212 24.5 28.8424 23.9816 28.9721 23.2683L31.2027 11H8.70279Z" fill="black" fill-opacity="0.57"/>
                    </svg>
                    <span>
                      Your cart is empty
                    </span>
                  </div>
                )}
                {cartItemsGroupByDesignName && Object.values(cartItemsGroupByDesignName).map((items) => (
                  <div class="cart-preview-design-group" key={items[0].design_name}>
                    <h4>{items[0].design_name}</h4>
                    {items.map((item) => (
                      <div class="cart-preview-item" key={item.design_composite_id}>
                        <div class="cart-preview-item-content">
                          <div class="cart-preview-item-image">
                            <img
                              loading="lazy"
                              src={`${Env.MMS_URL}/mms/images/catalog/colors/${item.color_id}/views/alt/front_medium_extended.png?design=${item.design_composite_id}&amp;digest=${item.created_at}`}
                            />
                          </div>
                          <div
                            class="cart-preview-item-product-name"
                            data-testid="project-item-product-name">
                            {item.style_name}
                          </div>
                          {this.pricing && (
                            <div class="cart-preview-item-price"
                              data-testid="project-item-price">
                              ${item.sub_total}
                            </div>
                          )}
                          <div class="cart-preview-item-details"
                            data-testid="project-item-details">
                            {item.color_name}
                          </div>
                          <div class="cart-preview-item-quantity-with-price"
                            data-testid="project-item-quantity-with-price">
                            Qty {item.quantity}
                            {this.pricing && (<span> & {formatPrice(item.unit_price)} each</span>)}
                          </div>
                          {this.detailedQuantities && (<div class="cart-preview-item-sizes"
                            data-testid="project-item-sizes">
                            {item.quote_quantities.YS && <span>{item.quote_quantities.YS} YS</span>}
                            {item.quote_quantities.YM && <span>{item.quote_quantities.YM} YM</span>}
                            {item.quote_quantities.YL && <span>{item.quote_quantities.YL} YL</span>}
                            {item.quote_quantities.XS && <span>{item.quote_quantities.XS} XS</span>}
                            {item.quote_quantities.S && <span>{item.quote_quantities.S} S</span>}
                            {item.quote_quantities.M && <span>{item.quote_quantities.M} M</span>}
                            {item.quote_quantities.L && <span>{item.quote_quantities.L} L</span>}
                            {item.quote_quantities.XL && <span>{item.quote_quantities.XL} XL</span>}
                            {item.quote_quantities.XXL && <span>{item.quote_quantities.XXL} XXL</span>}
                            {item.quote_quantities.XXXL && <span>{item.quote_quantities.XXXL} XXXL</span>}
                            {item.quote_quantities.XXXXL && <span>{item.quote_quantities.XXXXL} XXXXL</span>}
                            {item.quote_quantities.XXXXXL && <span>{item.quote_quantities.XXXXXL} XXXXXL</span>}
                          </div>)}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div slot="dialog-actions" class="cart-preview-actions">
              <hr />
              <div class="cart-preview-summary">
                <div>Total Styles
                  <span class="cart-count">{this.cart?.items.length ?? 0}</span>
                </div>
                {this.pricing && (
                  <div>Order Subtotal
                    <span>{formatPrice(this.cart?.items.reduce((acc,item) => acc + item.sub_total,0) ?? 0)}</span>
                  </div>
                )}
              </div>
              <button onClick={this.redirectToCart.bind(this)}>
                Review Cart & Check Out
              </button>
            </div>
          </ci-dialog>)}
      </Host>
    );
  }
}
